import React from "react";
import { Card, Tag, Divider, Progress, Alert } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { TestResultsSectionProps, InstallerTestSummary, InstallerTestStep } from "../../../../types/installationTest";
import "./TestResultsSection.scss";

export const TestResultsSection: React.FC<TestResultsSectionProps> = ({ 
  results
}) => {
  const { t } = useTranslation("settings");

  const renderResults = () => {
    if (!results) return null;

    let parsedResults: any = results;
    if (typeof results === "string") {
      try {
        parsedResults = JSON.parse(results);
      } catch (e) {
        return <div className="result-text">{results}</div>;
      }
    }

    if (parsedResults.summary) {
      return renderOfficialStructure(parsedResults as InstallerTestSummary);
    }

    return (
      <div className="test-results-formatted">
        <pre>{JSON.stringify(parsedResults, null, 2)}</pre>
      </div>
    );
  };

  const renderOfficialStructure = (data: InstallerTestSummary) => {
    const { summary, steps, details } = data;
    const statusColor = summary.status === "passed" ? "green" : summary.status === "failed" ? "red" : "blue";

    return (
      <div className="test-results-formatted">
        <div className="summary-section">
          <div className="summary-header">
            <div className="result-item">
              <span className="result-label">{t("test_status")}:</span>
              <Tag color={statusColor} style={{ fontSize: "14px", padding: "4px 12px" }}>
                {summary.status.toUpperCase()}
              </Tag>
            </div>
            <div className="result-item">
              <span className="result-label">{t("progress")}:</span>
              <Progress 
                percent={summary.progress_percent} 
                status={summary.status === "failed" ? "exception" : summary.status === "passed" ? "success" : "active"}
                style={{ flex: 1, marginLeft: "12px" }}
              />
            </div>
          </div>

          <Divider />

          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">{t("run_id")}:</span>
              <span className="stat-value">{summary.run_id}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("flags") || "Flags"}:</span>
              <span className="stat-value">{summary.flags}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("steps_total")}:</span>
              <span className="stat-value">{summary.steps_total}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("steps_attempted") || "Steps Attempted"}:</span>
              <span className="stat-value">{summary.steps_attempted}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("steps_passed")}:</span>
              <span className="stat-value success">{summary.steps_passed}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("steps_failed")}:</span>
              <span className="stat-value error">{summary.steps_failed}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("duration")}:</span>
              <span className="stat-value">{formatDuration(summary.duration_ms)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("started_us") || "Started (us)"}:</span>
              <span className="stat-value">{summary.started_us}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t("ended_us") || "Ended (us)"}:</span>
              <span className="stat-value">{summary.ended_us}</span>
            </div>
            {summary.current_step_index !== undefined && (
              <div className="stat-item">
                <span className="stat-label">{t("current_step_index") || "Current Step Index"}:</span>
                <span className="stat-value">{summary.current_step_index}</span>
              </div>
            )}
          </div>

          {summary.errors && summary.errors.error_list && summary.errors.error_list.length > 0 && (
            <>
              <Divider />
              <Alert
                message={t("errors")}
                description={
                  <div className="error-list">
                    {summary.errors.error_list.map((error: string, idx: number) => (
                      <Tag key={idx} color="red" style={{ margin: "4px" }}>
                        {error}
                      </Tag>
                    ))}
                  </div>
                }
                type="error"
                showIcon
              />
            </>
          )}
          {summary.errors && (
            <>
              <Divider />
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">{t("error_bits") || "Error Bits"}:</span>
                  <span className="stat-value">{summary.errors.bits}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">{t("error_hex") || "Error Hex"}:</span>
                  <span className="stat-value">{summary.errors.hex}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {steps && steps.length > 0 && (
          <>
            <Divider />
            <div className="steps-section">
              <h4>{t("test_steps") || "Test Steps"}</h4>
              <div className="steps-list">
                {steps.map((step: InstallerTestStep, idx: number) => (
                  <div key={idx} className={`step-item ${step.passed ? "passed" : step.attempted ? "failed" : "not-attempted"}`}>
                    <div className="step-header">
                      <div className="step-icon">
                        {step.passed ? (
                          <CheckCircleOutlined style={{ color: "#52c41a" }} />
                        ) : step.attempted ? (
                          <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                        ) : (
                          <span className="step-index">{step.index + 1}</span>
                        )}
                      </div>
                      <div className="step-name">{step.name}</div>
                      <Tag color={step.passed ? "green" : step.attempted ? "red" : "default"}>
                        {step.passed ? t("passed") || "PASSED" : step.attempted ? t("failed") || "FAILED" : t("not_attempted") || "NOT ATTEMPTED"}
                      </Tag>
                    </div>
                    {(step.value_str || step.value_i32 !== undefined || step.value_f32 !== undefined) && (
                      <div className="step-value">
                        {step.value_str && <span>{step.value_str}</span>}
                        {step.value_i32 !== undefined && <span>{step.value_i32}</span>}
                        {step.value_f32 !== undefined && <span>{step.value_f32.toFixed(2)}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {details && (
          <>
            <Divider />
            <div className="details-section">
              <h4>{t("test_details") || "Test Details"}</h4>
              
              {details.relay_test && (
                <div className="detail-item">
                  <h5>{t("relay_test") || "Relay Test"}</h5>
                  <div className="detail-content">
                    <div className="detail-row">
                      <span className="detail-label">{t("status") || "Status"}:</span>
                      <Tag color={details.relay_test.status === "passed" ? "green" : "red"}>
                        {details.relay_test.status.toUpperCase()}
                      </Tag>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("error_code") || "Error Code"}:</span>
                      <span>{details.relay_test.error_code}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("valve_feedback_ok") || "Valve Feedback OK"}:</span>
                      <span>{details.relay_test.valve_fb_ok ? t("yes") || "Yes" : t("no") || "No"}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("mean_delta_w") || "Mean Delta W"}:</span>
                      <span>{details.relay_test.mean_delta_w}</span>
                    </div>
                    {details.relay_test.phase_prob_miswire !== undefined && (
                      <div className="detail-row">
                        <span className="detail-label">{t("phase_prob_miswire") || "Phase Prob Miswire"}:</span>
                        <span>{details.relay_test.phase_prob_miswire}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {details.power_test && (
                <div className="detail-item">
                  <h5>{t("power_test") || "Power Test"}</h5>
                  <div className="detail-content">
                    <div className="detail-row">
                      <span className="detail-label">{t("status") || "Status"}:</span>
                      <Tag color={details.power_test.status === "passed" ? "green" : "red"}>
                        {details.power_test.status.toUpperCase()}
                      </Tag>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("baseline_w") || "Baseline W"}:</span>
                      <span>{details.power_test.baseline_w.toFixed(2)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("min_duty_found") || "Min Duty Found"}:</span>
                      <span>{details.power_test.min_duty_found}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("min_detect_w") || "Min Detect W"}:</span>
                      <span>{details.power_test.min_detect_w.toFixed(2)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("max_measured_w") || "Max Measured W"}:</span>
                      <span>{details.power_test.max_measured_w.toFixed(2)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("duty0_ok") || "Duty 0 OK"}:</span>
                      <span>{details.power_test.duty0_ok ? t("yes") || "Yes" : t("no") || "No"}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("min_ok") || "Min OK"}:</span>
                      <span>{details.power_test.min_ok ? t("yes") || "Yes" : t("no") || "No"}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("max_ok") || "Max OK"}:</span>
                      <span>{details.power_test.max_ok ? t("yes") || "Yes" : t("no") || "No"}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t("fail_err_code") || "Fail Error Code"}:</span>
                      <span>{details.power_test.fail_err_code}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };


  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  return (
    <div className="test-results-section">
      <h3>{t("test_results")}</h3>
      <Card className="test-results-card">
        <div className="test-results-content">
          {renderResults()}
        </div>
      </Card>
    </div>
  );
};

